<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { api } from '$lib/api/client';
  import { updateUserInStore, signOut } from '$lib/stores/auth';
  import type { User, Card, TachieSettings } from '$lib/api/types';
  import { getServiceIcon } from '$lib/utils/serviceIcons';
  import EditableField from '$components/profile/EditableField.svelte';
  import Avatar from '$components/profile/Avatar.svelte';

  export let user: User;
  export let isOwnPage: boolean;
  export let sidebarLinks: Card[] = []; // Link cards to show in sidebar
  export let draggingGridCard: Card | null = null; // Card being dragged from grid

  const dispatch = createEventDispatcher<{
    updated: User;
    linkDragStart: Card;
    linkDragEnd: void;
    cardDroppedToSidebar: string; // cardId
  }>();

  let isDragOver = false;
  let showTachieSettings = false;
  let tachieFileInput: HTMLInputElement;

  // Tachie settings with defaults
  $: tachieUrl = user.tachieUrls?.[0] || null;
  $: tachieOpacity = user.tachieSettings?.opacity ?? 30;
  $: tachiePosition = user.tachieSettings?.position ?? 'bottom';
  $: tachieSize = user.tachieSettings?.size ?? 'large';

  // CSS for tachie position
  $: tachiePositionStyle = {
    top: 'top-0',
    center: 'top-1/2 -translate-y-1/2',
    bottom: 'bottom-0',
  }[tachiePosition];

  // CSS for tachie size
  $: tachieSizeStyle = {
    small: 'h-1/3',
    medium: 'h-1/2',
    large: 'h-3/4',
    full: 'h-full',
  }[tachieSize];

  // Check if the dragging card can be dropped to sidebar
  $: canDropToSidebar = draggingGridCard?.type === 'link';
  $: isInvalidDrag = isDragOver && draggingGridCard && !canDropToSidebar;

  let draggingLink: Card | null = null;

  function handleLinkDragStart(event: DragEvent, link: Card) {
    if (!isOwnPage) return;
    draggingLink = link;
    event.dataTransfer?.setData('text/plain', link.id);
    event.dataTransfer!.effectAllowed = 'move';
    dispatch('linkDragStart', link);
  }

  function handleLinkDragEnd() {
    draggingLink = null;
    dispatch('linkDragEnd');
  }

  function handleSidebarDragOver(event: DragEvent) {
    if (!isOwnPage) return;
    // Only accept link cards
    const cardId = event.dataTransfer?.types.includes('text/plain');
    if (cardId) {
      event.preventDefault();
      isDragOver = true;
    }
  }

  function handleSidebarDragLeave() {
    isDragOver = false;
  }

  function handleSidebarDrop(event: DragEvent) {
    if (!isOwnPage) return;
    event.preventDefault();
    isDragOver = false;

    // Only accept link cards
    if (!canDropToSidebar) return;

    const cardId = event.dataTransfer?.getData('text/plain');
    if (cardId) {
      dispatch('cardDroppedToSidebar', cardId);
    }
  }

  async function handleDisplayNameChange(newValue: string) {
    try {
      const updated = await api.users.update({ displayName: newValue || null });
      updateUserInStore(updated);
      dispatch('updated', updated);
    } catch (error) {
      console.error('Failed to update display name:', error);
    }
  }

  async function handleBioChange(newValue: string) {
    try {
      const updated = await api.users.update({ bio: newValue || null });
      updateUserInStore(updated);
      dispatch('updated', updated);
    } catch (error) {
      console.error('Failed to update bio:', error);
    }
  }

  async function handleAvatarChange(url: string) {
    try {
      const updated = await api.users.update({ iconUrl: url });
      updateUserInStore(updated);
      dispatch('updated', updated);
    } catch (error) {
      console.error('Failed to update avatar:', error);
    }
  }

  async function handleSignOut() {
    await signOut();
    navigate('/');
  }

  // Tachie handlers
  async function handleTachieUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    try {
      const { url } = await api.upload.image(file, 'tachie');
      const updated = await api.users.update({ tachieUrls: [url] });
      updateUserInStore(updated);
      dispatch('updated', updated);
    } catch (error) {
      console.error('Failed to upload tachie:', error);
    }
  }

  async function handleTachieRemove() {
    try {
      const updated = await api.users.update({ tachieUrls: [] });
      updateUserInStore(updated);
      dispatch('updated', updated);
    } catch (error) {
      console.error('Failed to remove tachie:', error);
    }
  }

  async function handleTachieSettingsChange(newSettings: Partial<TachieSettings>) {
    try {
      const currentSettings = user.tachieSettings || {};
      const updated = await api.users.update({
        tachieSettings: { ...currentSettings, ...newSettings },
      });
      updateUserInStore(updated);
      dispatch('updated', updated);
    } catch (error) {
      console.error('Failed to update tachie settings:', error);
    }
  }
</script>

<aside
  class="profile-sidebar min-[1280px]:sticky min-[1280px]:top-0 min-[1280px]:h-screen transition-colors duration-150 relative overflow-hidden
    {isDragOver && canDropToSidebar ? 'ring-2 ring-purple-400 ring-inset bg-purple-50/50' : ''}
    {isInvalidDrag ? 'ring-2 ring-red-400 ring-inset bg-red-50/50 cursor-not-allowed' : ''}"
  on:dragover={handleSidebarDragOver}
  on:dragleave={handleSidebarDragLeave}
  on:drop={handleSidebarDrop}
  role="complementary"
>
  <!-- Tachie (standing art) background -->
  {#if tachieUrl}
    <div
      class="absolute left-0 right-0 pointer-events-none {tachiePositionStyle} {tachieSizeStyle}"
      style="opacity: {tachieOpacity / 100};"
    >
      <img
        src={tachieUrl}
        alt=""
        class="w-full h-full object-contain object-bottom"
      />
    </div>
  {/if}

  <!-- Invalid drop indicator -->
  {#if isInvalidDrag}
    <div class="absolute inset-0 flex items-center justify-center bg-red-100/80 z-50 pointer-events-none">
      <div class="text-red-500 text-center">
        <i class="lni lni-ban text-3xl"></i>
        <p class="text-sm mt-1">リンクカードのみ</p>
      </div>
    </div>
  {/if}
  <div class="relative z-10 flex flex-col items-center min-[1280px]:items-start text-center min-[1280px]:text-left">
    <!-- Avatar and Tachie button -->
    <div class="flex items-end gap-2">
      <Avatar
        src={user.iconUrl}
        alt={user.displayName || user.username}
        editable={isOwnPage}
        on:change={(e) => handleAvatarChange(e.detail)}
      />
      {#if isOwnPage}
        <div class="relative">
          <button
            on:click={() => showTachieSettings = !showTachieSettings}
            class="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 shadow-sm flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
            title="立ち絵設定"
          >
            <i class="lni lni-gallery text-lg"></i>
          </button>

          <!-- Tachie settings panel -->
          {#if showTachieSettings}
            <div class="absolute top-12 left-0 z-30 bg-white rounded-lg shadow-lg p-4 w-64">
              <h3 class="text-sm font-medium text-gray-900 mb-3">立ち絵設定</h3>

              <!-- Upload/Change button -->
              <input
                type="file"
                accept="image/*"
                class="hidden"
                bind:this={tachieFileInput}
                on:change={handleTachieUpload}
              />
              <div class="flex gap-2 mb-4">
                <button
                  on:click={() => tachieFileInput.click()}
                  class="flex-1 px-3 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  {tachieUrl ? '変更' : 'アップロード'}
                </button>
                {#if tachieUrl}
                  <button
                    on:click={handleTachieRemove}
                    class="px-3 py-2 text-sm bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                  >
                    削除
                  </button>
                {/if}
              </div>

              {#if tachieUrl}
                <!-- Opacity slider -->
                <div class="mb-4">
                  <label class="text-xs text-gray-500 mb-1 block">
                    透明度: {tachieOpacity}%
                    <input
                      type="range"
                      min="10"
                      max="100"
                      value={tachieOpacity}
                      on:change={(e) => handleTachieSettingsChange({ opacity: parseInt(e.currentTarget.value) })}
                      class="w-full mt-1"
                    />
                  </label>
                </div>

                <!-- Position select -->
                <div class="mb-4">
                  <span class="text-xs text-gray-500 mb-1 block">位置</span>
                  <div class="flex gap-1">
                    <button
                      on:click={() => handleTachieSettingsChange({ position: 'top' })}
                      class="flex-1 px-2 py-1 text-xs rounded {tachiePosition === 'top' ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}"
                    >上</button>
                    <button
                      on:click={() => handleTachieSettingsChange({ position: 'center' })}
                      class="flex-1 px-2 py-1 text-xs rounded {tachiePosition === 'center' ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}"
                    >中央</button>
                    <button
                      on:click={() => handleTachieSettingsChange({ position: 'bottom' })}
                      class="flex-1 px-2 py-1 text-xs rounded {tachiePosition === 'bottom' ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}"
                    >下</button>
                  </div>
                </div>

                <!-- Size select -->
                <div>
                  <span class="text-xs text-gray-500 mb-1 block">サイズ</span>
                  <div class="flex gap-1">
                    <button
                      on:click={() => handleTachieSettingsChange({ size: 'small' })}
                      class="flex-1 px-2 py-1 text-xs rounded {tachieSize === 'small' ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}"
                    >小</button>
                    <button
                      on:click={() => handleTachieSettingsChange({ size: 'medium' })}
                      class="flex-1 px-2 py-1 text-xs rounded {tachieSize === 'medium' ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}"
                    >中</button>
                    <button
                      on:click={() => handleTachieSettingsChange({ size: 'large' })}
                      class="flex-1 px-2 py-1 text-xs rounded {tachieSize === 'large' ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}"
                    >大</button>
                    <button
                      on:click={() => handleTachieSettingsChange({ size: 'full' })}
                      class="flex-1 px-2 py-1 text-xs rounded {tachieSize === 'full' ? 'bg-blue-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}"
                    >全体</button>
                  </div>
                </div>
              {/if}

              <!-- Close button -->
              <button
                on:click={() => showTachieSettings = false}
                class="mt-4 w-full px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                閉じる
              </button>
            </div>
          {/if}
        </div>
      {/if}
    </div>

    <!-- Display Name -->
    <div class="mt-4 w-full">
      {#if isOwnPage}
        <EditableField
          value={user.displayName || ''}
          placeholder="表示名を入力"
          class="text-xl font-bold text-gray-900"
          on:change={(e) => handleDisplayNameChange(e.detail)}
        />
      {:else}
        <h1 class="text-xl font-bold text-gray-900">
          {user.displayName || user.username}
        </h1>
      {/if}
    </div>

    <!-- Username -->
    <p class="text-gray-500 text-sm">@{user.username}</p>

    <!-- Bio -->
    <div class="mt-3 w-full">
      {#if isOwnPage}
        <EditableField
          value={user.bio || ''}
          placeholder="自己紹介を入力"
          multiline
          class="text-gray-600 text-sm"
          on:change={(e) => handleBioChange(e.detail)}
        />
      {:else if user.bio}
        <p class="text-gray-600 text-sm whitespace-pre-wrap">{user.bio}</p>
      {/if}
    </div>

    <!-- Sidebar Links -->
    {#if sidebarLinks.length > 0}
      <div class="mt-6 flex flex-wrap gap-3 justify-center min-[1280px]:justify-start">
        {#each sidebarLinks as link}
          {@const serviceIcon = getServiceIcon(link.data.url)}
          {@const tooltipText = link.data.comment || link.ogp?.title || link.data.url}
          <div class="relative group">
            <a
              href={isOwnPage ? undefined : link.data.url}
              target="_blank"
              rel="noopener noreferrer"
              class="w-12 h-12 rounded-full bg-white shadow-md hover:shadow-lg transition-shadow flex items-center justify-center overflow-hidden border border-gray-100 {isOwnPage ? 'cursor-grab active:cursor-grabbing' : ''} {draggingLink?.id === link.id ? 'opacity-50' : ''}"
              draggable={isOwnPage}
              on:dragstart={(e) => handleLinkDragStart(e, link)}
              on:dragend={handleLinkDragEnd}
            >
              {#if serviceIcon}
                <i class="lni {serviceIcon} text-xl text-gray-700 pointer-events-none"></i>
              {:else if link.ogp?.favicon}
                <img src={link.ogp.favicon} alt="" class="w-6 h-6 object-contain pointer-events-none" />
              {:else}
                <i class="lni lni-link-2-angular-right text-xl text-gray-400 pointer-events-none"></i>
              {/if}
            </a>
            <!-- Custom Tooltip -->
            <div class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-1.5 bg-gray-800 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
              {tooltipText}
              <div class="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-gray-800"></div>
            </div>
          </div>
        {/each}
      </div>
    {/if}

    <!-- Logout Button (only for own page) -->
    {#if isOwnPage}
      <button
        on:click={handleSignOut}
        class="mt-6 px-4 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
      >
        ログアウト
      </button>
    {/if}
  </div>
</aside>
